import agent from '../agent';
import { syncDbToLocalStorage } from '../utils/datatable-sync';
import * as Sentry from '@sentry/vue';

let pendingSetUserRequest = null;

const ensureArray = value => {
    return Array.isArray(value) ? value : [];
};

const normalizeUserPayload = (payload = {}, baseUser = {}) => {
    return {
        ...baseUser,
        ...payload,
        roles: ensureArray(payload.roles ?? baseUser.roles),
        user_permissions: ensureArray(payload.user_permissions ?? baseUser.user_permissions),
        user_groups: ensureArray(payload.user_groups ?? baseUser.user_groups),
        permissions: ensureArray(payload.permissions ?? baseUser.permissions),
        own_relation_ids: ensureArray(payload.own_relation_ids ?? baseUser.own_relation_ids),
        preferences:
            payload.preferences && typeof payload.preferences === 'object'
                ? payload.preferences
                : baseUser.preferences || {},
    };
};

const getInitialLang = () => {
    const requestedLang = typeof requested_language !== 'undefined' ? requested_language : '';
    if (requestedLang) {
        return (Array.isArray(languages) ? languages : JSON.parse(languages.innerText)).find(
            language => language.code === requestedLang
        );
    }

    const savedLang = localStorage.getItem('lang');
    if (savedLang) return JSON.parse(savedLang);

    const langs = Array.isArray(languages) ? languages : JSON.parse(languages.innerText);
    return langs.find(l => l.code === user_lang) || langs[0];
};

const initialState = {
    status: { loggedIn: false },
    user: {
        first_name: '',
        last_name: '',
        full_name: '',
        email: '',
        last_login: '',
        date_joined: '',
        phone: '',
        roles: [],
        user_permissions: [],
        user_groups: [],
        permissions: [],
        own_relation_ids: [],
        emailhas_scanned_qr: false,
        is_active: false,
        is_superuser: false,
        lang: getInitialLang(),
        id: null,
        avatar: null,
        preferences: {},
    },
    available_langs: languages,
    availableEstablishments: 0,
    datatablesFirstSyncDone: false,
};
export const auth = {
    namespaced: true,
    state: initialState,
    getters: {
        user: state => {
            return state.user;
        },
        available_langs: state => {
            return state.available_langs;
        },
        availableEstablishments: state => {
            return state.availableEstablishments;
        },
    },
    actions: {
        setUser({ commit }) {
            if (pendingSetUserRequest) {
                return pendingSetUserRequest;
            }

            pendingSetUserRequest = agent.User.getCurrentUser()
                .then(response => {
                    const user = response.data;

                    commit('setUser', user);
                    syncDbToLocalStorage(user);
                    commit('setDatatablesFirstSyncDone', true);

                    Sentry.setUser({
                        id: user.id ? String(user.id) : undefined,
                        email: user.email || undefined,
                        username: user.username || user.name || undefined,
                    });

                    Sentry.setTag('app_section', 'waste-desk');

                    // if (user.role) {
                    //     Sentry.setTag('user_role', user.role);
                    // }

                    Sentry.setContext('user', {
                        id: user.id || null,
                        email: user.email || null,
                        username: user.username || user.name || null,
                        // role: user.role || null,
                    });

                    return user;
                })
                .catch(error => {
                    commit('setUserFailure');
                    throw error;
                });
            return pendingSetUserRequest.finally(() => {
                pendingSetUserRequest = null;
            });
        },
        updateUser({ commit }, user) {
            let type = user.updType ? user.updType : 'full';
            let updateTypes = {
                full: 'update',
                partial: 'partialUpdate',
            };
            if (user.updType) {
                delete user.updType;
            }
            return agent.User[updateTypes[type]](user)
                .then(response => {
                    commit('updateUser', response.data);
                    return Promise.resolve(response.data);
                })
                .catch(error => {
                    commit('setUserFailure');
                    return Promise.reject(error);
                });
        },
        setUserLang({ commit }, lang) {
            return agent.Language.setLanguage(lang.code).then(() => {
                commit('setUserLang', lang);
            });
        },
        setAvailableEstablishments({ commit }) {
            agent.Establishment.getActiveEstablishments().then(response => {
                commit('setAvailableEstablishments', response.data);
            });
        },
        updateAvailableEstablishments({ commit }, establishmentsCount) {
            commit('updateAvailableEstablishments', establishmentsCount);
        },
    },
    mutations: {
        setUser(state, user) {
            state.status.loggedIn = true;
            const currentLang = JSON.parse(localStorage.getItem('lang')) || getInitialLang();
            state.user = {
                ...normalizeUserPayload(user, initialState.user),
                lang: currentLang,
            };
        },
        updateUser(state, user) {
            state.status.loggedIn = true;
            const currentLang = JSON.parse(localStorage.getItem('lang')) || getInitialLang();
            state.user = {
                ...normalizeUserPayload(user, state.user),
                lang: currentLang,
            };
        },
        setUserLang(state, lang) {
            state.user.lang = lang;
            localStorage.setItem('lang', JSON.stringify(lang));
        },
        setUserFailure(state) {
            state.status.loggedIn = false;
            Sentry.setUser(null);
            state.user = initialState.user;
            // router.push('/login')
        },
        setAvailableEstablishments(state, establishments) {
            state.availableEstablishments = establishments.length;
        },
        updateAvailableEstablishments(state, establishmentsCount) {
            state.availableEstablishments = establishmentsCount;
        },
        setDatatablesFirstSyncDone(state, done) {
            state.datatablesFirstSyncDone = done;
        },
    },
};
